import React from 'react';
import { useNavigate } from 'react-router-dom';

const steps = [
  {
    title: 'Install the Extension',
    body: 'A VS Code extension runs silently in the background, detecting file edits, save events, and idle periods.',
    icon: '◌',
  },
  {
    title: 'Data Flows to Supabase',
    body: 'Every 15 seconds, batched activity events are securely flushed to your Supabase workspace - idempotent, privacy-first, no code content ever.',
    icon: '⇄',
  },
  {
    title: "See Your Team's Pulse",
    body: 'The FounderPulse dashboard aggregates daily summaries and surfaces who is coding what, when, and for how long.',
    icon: '▣',
  },
];

const features = [
  ['⏱', 'Real-time tracking', 'Live time spent coding and switching contexts.'],
  ['🔒', 'Privacy-first', 'Tracks activity, never code content.'],
  ['📊', 'Daily coding trends', 'See output over time without manual reporting.'],
  ['👥', 'Team leaderboard', 'Compare contributors by active hours and pace.'],
  ['🗂', 'Per-project breakdown', 'Understand effort across workstreams.'],
  ['🌐', 'Supabase-powered', 'Your data, your control, your schema.'],
];

const MARKETPLACE_URL = 'https://marketplace.visualstudio.com/items?itemName=GloroSolutions.founderpulse-time-tracker';
const BRAND_LOGO_SRC = '/founderpulselogo.png';

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="landing-page surface-noise">
      <header className="landing-header">
        <div className="brand-lockup">
          <div className="brand-mark">
            <img className="brand-mark__image" src={BRAND_LOGO_SRC} alt="FounderPulse logo" />
          </div>
          <div className="brand-copy">
            <span>FounderPulse</span>
          </div>
        </div>

        <nav className="landing-nav" aria-label="Landing navigation">
          <a href="#how-it-works">How it works</a>
          <a href="#features">Features</a>
          <a href="#install-extension">Install</a>
        </nav>

        <div className="landing-actions">
          <button className="button button--ghost button--mono" type="button" onClick={() => navigate('/login')}>
            Sign In
          </button>
          <button className="button button--solid button--mono" type="button" onClick={() => navigate('/login?mode=signup')}>
            Get Started
          </button>
        </div>
      </header>

      <main>
        <section className="hero-section">
          <div className="hero-glow" />
          <div className="hero-copy">
            <h1>Know exactly who built what, and when.</h1>
            <p>
              FounderPulse tracks VS Code activity across your co-founder team - file edits,
              coding hours, idle time - and surfaces it in a shared analytics dashboard. No
              estimates. No self-reporting. Real data.
            </p>
            <div className="hero-cta-row">
              <button className="button button--solid button--mono" type="button" onClick={() => navigate('/login')}>
                Sign In →
              </button>
              <a className="button button--marketplace button--mono" href={MARKETPLACE_URL} target="_blank" rel="noreferrer">
                <span className="ms-logo" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                </span>
                Install Extension
              </a>
              <a className="text-link" href="#how-it-works">
                See how it works ↓
              </a>
            </div>
          </div>
        </section>

        <section className="marketplace-poster" id="install-extension" aria-label="Install FounderPulse VS Code extension">
          <div className="marketplace-poster__badge">VS CODE MARKETPLACE</div>
          <h2>Install the FounderPulse extension from VS Code.</h2>
          <p>
            Install in one click and start tracking coding activity instantly. Built for co-founders who want transparent, real-time output analytics.
          </p>
          <a className="button button--marketplace button--mono" href={MARKETPLACE_URL} target="_blank" rel="noreferrer">
            <span className="ms-logo" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </span>
            Install Extension
          </a>
        </section>

        <section className="section-block" id="how-it-works">
          <div className="section-kicker">HOW IT WORKS</div>
          <div className="section-heading-row">
            <h2>One stream, three layers of signal.</h2>
          </div>
          <div className="steps-grid">
            {steps.map((step, index) => (
              <article className="info-card info-card--step" key={step.title} style={{ animationDelay: `${index * 80}ms` }}>
                <div className="icon-badge">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section-block" id="features">
          <div className="section-kicker">WHAT YOU GET</div>
          <div className="section-heading-row">
            <h2>Designed for people who read dashboards, not slide decks.</h2>
          </div>
          <div className="feature-grid">
            {features.map(([icon, title, description], index) => (
              <article className="feature-tile" key={title} style={{ animationDelay: `${index * 80}ms` }}>
                <div className="feature-icon">{icon}</div>
                <div>
                  <div className="feature-label">{title}</div>
                  <p>{description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="cta-banner">
          <div>
            <h2>Ready to see your team&apos;s pulse?</h2>
          </div>
          <button className="button button--solid button--mono" type="button" onClick={() => navigate('/login')}>
            Sign In
          </button>
        </section>
      </main>

      <footer className="landing-footer">
        FounderPulse · Built for co-founders · MIT License · Built by{' '}
        <a href="https://mathewjolly.vercel.app" target="_blank" rel="noreferrer">
          Mathew Jolly
        </a>
      </footer>
    </div>
  );
}
